import type { Metric } from '../types/presentation.types';

function asNumber(value: string | number): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value.trim())) {
    const n = Number(value.trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function formatCount(n: number): string {
  return String(Math.round(n));
}

function formatDecimal(n: number, digits = 1): string {
  const fixed = n.toFixed(digits);
  return fixed.replace(/\.0+$/, '').replace(/(\.\d*[1-9])0+$/, '$1');
}

/**
 * Display value + unit for Ask Tia metric tiles.
 * Infers missing units from the label (response time → d, rates → %)
 * and formats whole counts as integers.
 */
export function formatMetricDisplay(metric: Metric): {
  value: string;
  unit?: string;
} {
  const label = metric.label.toLowerCase();
  const num = asNumber(metric.value);
  let unit = metric.unit?.trim() || undefined;

  if (num === null) {
    return { value: String(metric.value), unit };
  }

  const isRate = /(rate|percent|completion|conversion)/.test(label);
  const isResponseTime = /response\s*time/.test(label);

  if (isRate) {
    unit = unit || '%';
    return { value: formatDecimal(num), unit };
  }
  if (isResponseTime) {
    unit = unit || 'd';
    return { value: formatDecimal(num), unit };
  }

  // Counts / todos / open items: drop trailing .0
  if (Number.isInteger(num) || Math.abs(num - Math.round(num)) < 1e-9) {
    return { value: formatCount(num), unit };
  }

  return { value: formatDecimal(num), unit };
}
